import {PayloadAction, createSelector, createSlice} from '@reduxjs/toolkit';

import {storage} from '@edna/utils';

import {EStorageKeys, TItemId} from 'src/constants';
import {listenerMiddleware} from 'src/models';

import {TBannerCategory} from './definitions';

type TBanner = Partial<Record<TItemId, {isOpen: boolean}>>;
type TBannersState = Partial<Record<TBannerCategory, TBanner>>;

const bannersConfigSlice = createSlice({
  name: 'bannersConfig',
  initialState: () => ({
    config: (storage.get<TBannersState>(EStorageKeys.BANNERS) as TBannersState) ?? {},
  }),
  reducers: {
    closeBanner(state, action: PayloadAction<{id: TItemId; category: TBannerCategory}>) {
      const category = state.config[action.payload.category] ?? {};

      state.config[action.payload.category] = {...category, [action.payload.id]: {isOpen: false}};
    },
    resetAllBanners() {
      return {
        config: {},
      };
    },
  },
});

export const {closeBanner, resetAllBanners} = bannersConfigSlice.actions;

const selector = (state: TRootState) => state;

export const selectIsBannerOpen = ({id, category}: {id?: TItemId; category?: TBannerCategory}) => {
  return createSelector(selector, (state) =>
    id && category ? state.bannersConfig.config[category]?.[id]?.isOpen ?? true : true,
  );
};

export const selectClosedBannersIds = (category: TBannerCategory) =>
  createSelector(selector, (state) => {
    const allByCategory = state.bannersConfig.config[category];

    return allByCategory
      ? Object.entries(allByCategory)
          .filter(([, value]) => !value?.isOpen)
          .map(([id]) => id)
      : [];
  });

listenerMiddleware.startListening({
  predicate: (_, currentState, previousState) => currentState !== previousState,
  effect: (_, listenerApi) =>
    storage.set(EStorageKeys.BANNERS, listenerApi.getState().bannersConfig.config),
});

export default bannersConfigSlice.reducer;
